import React, { useState, useRef } from 'react';
import { 
  ArrowLeft, Calendar, Users, Code, Layout, FileText, Search, 
  Smartphone, Globe, Settings, CheckCircle, Zap, Target, 
  Database, Shield, Palette, Cpu, Clock, TrendingUp,
  Play, Pause, Monitor, Maximize2, Video, ChevronRight,
  Award, Eye, MessageCircle, Mail, Phone, Wifi, Languages,
  Battery, Droplets, Zap as Lightning, Cloud, Building,
  BarChart, ShieldCheck, Server, Cpu as CpuIcon,
  Globe as GlobeIcon, Package, Filter, MessageSquare,
  Download, MapPin, ExternalLink, Crown, Users as UsersIcon,
  ShoppingCart, CreditCard, Truck, Bell, UserCheck,
  BarChart3, Settings as SettingsIcon, Lock, Mail as MailIcon,
  Smartphone as SmartphoneIcon, Globe as WebIcon
} from 'lucide-react';

export default function FPMPlatform({ darkMode, language, onBack }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const videoRef = useRef(null);
  
  const isRTL = language === 'ar';

  const handleVideoPlay = () => {
    if (videoRef.current) {
      if (playingVideo) {
        videoRef.current.pause();
        setPlayingVideo(null);
      } else {
        videoRef.current.play();
        setPlayingVideo(true);
      }
    }
  };

  const handleVideoPause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setPlayingVideo(false);
    }
  };

  const content = {
    fr: {
      title: 'Plateforme de Commandes FPM Maroc',
      subtitle: 'Système de gestion de commandes avec notifications WhatsApp et suivi en temps réel',
      backButton: 'Retour aux projets',
      
      // Navigation
      navOverview: 'Vue d\'ensemble',
      navDemo: 'Démonstration',
      navFeatures: 'Fonctionnalités',
      navTech: 'Technologies',
      navSeo: 'API & Intégrations',
      navResults: 'Résultats',
      
      // Hero Stats
      stats: [
        { value: '286+', label: 'Commandes', icon: ShoppingCart },
        { value: '93%', label: 'Prêtes', icon: CheckCircle },
        { value: '2', label: 'Utilisateurs', icon: Users },
        { value: '100%', label: 'Temps réel', icon: Clock }
      ],
      
      // Overview
      overviewTitle: 'Plateforme de Gestion des Commandes',
      overviewIntro: 'Développement d\'une plateforme complète de gestion de commandes pour FPM Maroc. Système avec tableau de bord administrateur, suivi des statuts en temps réel et notifications automatiques via WhatsApp.',
      
      projectGoals: 'Objectifs du Projet',
      goalsList: [
        'Créer un système centralisé de gestion des commandes',
        'Implémenter les notifications WhatsApp automatiques',
        'Fournir un tableau de bord administrateur complet',
        'Permettre le suivi en temps réel des statuts',
        'Gérer les utilisateurs et leurs commandes'
      ],
      
      myRole: 'Mon Rôle - Développeur Full Stack Solo',
      roleDetails: [
        'Conception de l\'architecture de la base de données',
        'Développement du backend avec API REST',
        'Création du dashboard administrateur',
        'Intégration de l\'API WhatsApp',
        'Mise en place du système d\'authentification',
        'Déploiement et configuration du serveur'
      ],
      
      // Demonstration Section
      demoTitle: 'Démonstration Complète',
      demoSubtitle: 'Découvrez la plateforme en action : gestion, notifications et suivi',
      
      videoTitle: 'Tour d\'horizon de la plateforme',
      videoDescription: 'Cette démo montre les fonctionnalités clés : création de commandes, changement de statut, notifications WhatsApp et tableau de bord.',
      
      keyFeatures: 'Points clés démontrés',
      keyFeaturesList: [
        'Création et gestion des commandes',
        'Changement de statut en temps réel',
        'Notifications automatiques WhatsApp',
        'Tableau de bord statistique',
        'Gestion des utilisateurs'
      ],
      
      // Features Section
      featuresTitle: 'Fonctionnalités Avancées',
      featuresSubtitle: 'Système complet pour la gestion commerciale',
      
      featuresList: [
        {
          icon: ShoppingCart,
          title: 'Gestion des Commandes',
          description: 'Interface complète pour créer, modifier et suivre les commandes clients',
          details: [
            'Création rapide de nouvelles commandes',
            'Statuts : Attente, En cours, Prêt, Livré',
            'Historique complet des modifications',
            'Recherche et filtrage avancés',
            'Export des données'
          ]
        },
        {
          icon: Bell,
          title: 'Notifications WhatsApp',
          description: 'Système automatique d\'envoi de notifications aux clients',
          details: [
            'Notification à chaque changement de statut',
            'Messages personnalisés selon le statut',
            'Support multilingue (FR/AR)',
            'Historique des notifications envoyées',
            'Configuration flexible des templates'
          ]
        },
        {
          icon: BarChart3,
          title: 'Tableau de Bord Admin',
          description: 'Vue d\'ensemble complète avec statistiques en temps réel',
          details: [
            'Nombre total de commandes',
            'Répartition par statut',
            'Performance des utilisateurs',
            'Graphiques et visualisations',
            'Alertes et notifications'
          ]
        },
        {
          icon: UserCheck,
          title: 'Gestion des Utilisateurs',
          description: 'Système d\'authentification et de gestion des accès',
          details: [
            'Connexion sécurisée',
            'Rôles et permissions',
            'Suivi des activités',
            'Profil utilisateur',
            'Gestion des sessions'
          ]
        },
        {
          icon: SettingsIcon,
          title: 'Configuration Flexible',
          description: 'Paramétrage avancé de la plateforme',
          details: [
            'Configuration des statuts',
            'Gestion des templates de messages',
            'Paramètres de notification',
            'Options d\'export',
            'Backup automatique'
          ]
        },
        {
          icon: Shield,
          title: 'Sécurité Entreprise',
          description: 'Protection des données clients et commerciales',
          details: [
            'HTTPS avec certificat SSL',
            'Authentification sécurisée',
            'Protection contre les injections',
            'Sauvegardes quotidiennes',
            'Audit de sécurité'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'Architecture Technique',
      techSubtitle: 'Stack moderne pour une plateforme professionnelle',
      
      frontendTech: {
        title: 'Frontend',
        technologies: [
          { name: 'Laravel Blade', purpose: 'Templates dynamiques' },
          { name: 'Tailwind CSS', purpose: 'Design system responsive' },
          { name: 'Alpine.js', purpose: 'Interactivité légère' },
          { name: 'Chart.js', purpose: 'Visualisations statistiques' }
        ]
      },
      
      backendTech: {
        title: 'Backend',
        technologies: [
          { name: 'Laravel 10', purpose: 'Framework PHP avec API' },
          { name: 'MySQL', purpose: 'Base de données relationnelle' },
          { name: 'Redis', purpose: 'Cache et sessions' },
          { name: 'Eloquent ORM', purpose: 'Modélisation des données' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps & Services',
        technologies: [
          { name: 'Git', purpose: 'Versioning du code' },
          { name: 'Composer/NPM', purpose: 'Gestion des dépendances' },
          { name: 'WhatsApp Business API', purpose: 'Notifications' },
          { name: 'Backups automatisés', purpose: 'Sauvegarde quotidienne' }
        ]
      },
      
      // API Section
      apiTitle: 'API & Intégrations',
      apiSubtitle: 'Système d\'intégration avancé',
      
      apiStrategy: 'Architecture API',
      strategyPoints: [
        {
          title: 'API REST',
          items: [
            'Endpoints pour la gestion des commandes',
            'Webhooks pour les notifications',
            'Documentation Swagger complète',
            'Authentification par token',
            'Rate limiting intelligent'
          ]
        },
        {
          title: 'Intégrations',
          items: [
            'WhatsApp Business API',
            'SMS gateway pour backup',
            'Email notifications',
            'Export vers Excel/PDF',
            'Webhooks externes'
          ]
        },
        {
          title: 'Performance',
          items: [
            'Temps de réponse < 500ms',
            'Support WebSocket pour temps réel',
            'Cache Redis pour performances',
            'Compression GZIP',
            'Load balancing'
          ]
        }
      ],
      
      performanceMetrics: 'Indicateurs Clés API',
      metrics: [
        { metric: 'Temps de Réponse API', value: '120ms', target: '<500ms', status: 'excellent' },
        { metric: 'Disponibilité', value: '99.9%', target: '>99.5%', status: 'excellent' },
        { metric: 'Notifications WhatsApp', value: '2s', target: '<5s', status: 'excellent' },
        { metric: 'Concurrent Users', value: '50+', target: '>20', status: 'excellent' }
      ],
      
      // Results
      resultsTitle: 'Impact & Résultats',
      resultsSubtitle: 'Performance commerciale mesurable',
      
      achievements: 'Réalisations Clés',
      achievementsList: [
        {
          metric: 'Commandes Gérées',
          description: 'Nombre total de commandes traitées',
          value: '286+',
          icon: ShoppingCart
        },
        {
          metric: 'Notifications',
          description: 'Messages WhatsApp envoyés automatiquement',
          value: '100%',
          icon: Bell
        },
        {
          metric: 'Efficacité',
          description: 'Commandes prêtes traitées',
          value: '93%',
          icon: CheckCircle
        },
        {
          metric: 'Performance',
          description: 'Score de satisfaction utilisateur',
          value: '95/100',
          icon: Zap
        }
      ],
      
      clientTestimonial: {
        quote: 'La plateforme a révolutionné notre gestion des commandes. Les notifications WhatsApp automatiques ont réduit nos appels de suivi de 80% et les clients apprécient le suivi en temps réel de leurs commandes.',
        author: 'Responsable Commercial',
        position: 'FPM Maroc'
      },
      
      keySuccess: 'Facteurs de Succès',
      successPoints: [
        'Interface intuitive nécessitant peu de formation',
        'Notifications en temps réel réduisant le support client',
        'Tableau de bord complet pour la prise de décision',
        'Sécurité renforcée pour les données clients',
        'Support réactif et améliorations continues'
      ],
      
      projectInfo: {
        status: 'En Production',
        team: 'Développeur solo',
        stack: 'Laravel, MySQL, WhatsApp API'
      },
      
      // Video section
      playVideo: 'Lancer la démonstration',
      pauseVideo: 'Mettre en pause',
      videoPlaying: 'En cours de lecture...'
    },
    ar: {
      title: 'منصة طلبات FPM المغرب',
      subtitle: 'نظام إدارة الطلبات مع إشعارات الواتساب والتتبع في الوقت الفعلي',
      backButton: 'العودة للمشاريع',
      
      // Navigation
      navOverview: 'نظرة عامة',
      navDemo: 'التجربة',
      navFeatures: 'المميزات',
      navTech: 'التقنيات',
      navSeo: 'API والتكاملات',
      navResults: 'النتائج',
      
      // Hero Stats
      stats: [
        { value: '286+', label: 'الطلبات', icon: ShoppingCart },
        { value: '93%', label: 'جاهزة', icon: CheckCircle },
        { value: '2', label: 'المستخدمين', icon: Users },
        { value: '100%', label: 'وقت حقيقي', icon: Clock }
      ],
      
      // Overview
      overviewTitle: 'منصة إدارة الطلبات',
      overviewIntro: 'تطوير منصة كاملة لإدارة الطلبات لـ FPM المغرب. نظام مع لوحة تحكم إدارية، تتبع الحالات في الوقت الفعلي وإشعارات تلقائية عبر الواتساب.',
      
      projectGoals: 'أهداف المشروع',
      goalsList: [
        'إنشاء نظام مركزي لإدارة الطلبات',
        'تنفيذ إشعارات الواتساب التلقائية',
        'توفير لوحة تحكم إدارية كاملة',
        'تمكين التتبع في الوقت الفعلي للحالات',
        'إدارة المستخدمين وطلباتهم'
      ],
      
      myRole: 'دوري - مطور Full Stack منفرد',
      roleDetails: [
        'تصميم بنية قاعدة البيانات',
        'تطوير الخادم الخلفي مع API REST',
        'إنشاء لوحة تحكم الإدارة',
        'تكامل واجهة برمجة تطبيقات الواتساب',
        'إعداد نظام المصادقة',
        'النشر وتهيئة الخادم'
      ],
      
      // Demonstration Section
      demoTitle: 'تجربة كاملة',
      demoSubtitle: 'اكتشف المنصة أثناء العمل: الإدارة، الإشعارات والتتبع',
      
      videoTitle: 'جولة في المنصة',
      videoDescription: 'هذه التجربة تظهر الميزات الرئيسية: إنشاء الطلبات، تغيير الحالة، إشعارات الواتساب ولوحة التحكم.',
      
      keyFeatures: 'النقاط الرئيسية المقدمة',
      keyFeaturesList: [
        'إنشاء وإدارة الطلبات',
        'تغيير الحالة في الوقت الفعلي',
        'إشعارات تلقائية بالواتساب',
        'لوحة تحكم إحصائية',
        'إدارة المستخدمين'
      ],
      
      // Features Section
      featuresTitle: 'ميزات متقدمة',
      featuresSubtitle: 'نظام كامل للإدارة التجارية',
      
      featuresList: [
        {
          icon: ShoppingCart,
          title: 'إدارة الطلبات',
          description: 'واجهة كاملة لإنشاء، تعديل وتتبع طلبات العملاء',
          details: [
            'إنشاء سريع لطلبات جديدة',
            'الحالات: انتظار، قيد المعالجة، جاهزة، مسلمة',
            'سجل كامل للتعديلات',
            'بحث وتصفية متقدم',
            'تصدير البيانات'
          ]
        },
        {
          icon: Bell,
          title: 'إشعارات الواتساب',
          description: 'نظام تلقائي لإرسال الإشعارات للعملاء',
          details: [
            'إشعار عند كل تغيير في الحالة',
            'رسائل مخصصة حسب الحالة',
            'دعم متعدد اللغات (فرنسي/عربي)',
            'سجل الإشعارات المرسلة',
            'تكوين مرن للقوالب'
          ]
        },
        {
          icon: BarChart3,
          title: 'لوحة تحكم الإدارة',
          description: 'نظرة عامة كاملة مع إحصائيات في الوقت الفعلي',
          details: [
            'عدد الطلبات الإجمالي',
            'توزيع حسب الحالة',
            'أداء المستخدمين',
            'رسوم بيانية وتصورات',
            'تنبيهات وإشعارات'
          ]
        },
        {
          icon: UserCheck,
          title: 'إدارة المستخدمين',
          description: 'نظام مصادقة وإدارة الوصول',
          details: [
            'اتصال آمن',
            'الأدوار والصلاحيات',
            'تتبع الأنشطة',
            'ملف المستخدم',
            'إدارة الجلسات'
          ]
        },
        {
          icon: SettingsIcon,
          title: 'تكوين مرن',
          description: 'إعدادات متقدمة للمنصة',
          details: [
            'تكوين الحالات',
            'إدارة قوالب الرسائل',
            'إعدادات الإشعارات',
            'خيارات التصدير',
            'نسخ احتياطي تلقائي'
          ]
        },
        {
          icon: Shield,
          title: 'أمان المؤسسة',
          description: 'حماية بيانات العملاء والتجارية',
          details: [
            'HTTPS مع شهادة SSL',
            'مصادقة آمنة',
            'حماية ضد الحقن',
            'نسخ احتياطي يومي',
            'تدقيق أمني'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'البنية التقنية',
      techSubtitle: 'مجموعة حديثة لمنصة احترافية',
      
      frontendTech: {
        title: 'الواجهة الأمامية',
        technologies: [
          { name: 'Laravel Blade', purpose: 'قوالب ديناميكية' },
          { name: 'Tailwind CSS', purpose: 'نظام تصميم متجاوب' },
          { name: 'Alpine.js', purpose: 'تفاعلية خفيفة' },
          { name: 'Chart.js', purpose: 'تصورات إحصائية' }
        ]
      },
      
      backendTech: {
        title: 'الخادم الخلفي',
        technologies: [
          { name: 'Laravel 10', purpose: 'إطار عمل PHP مع API' },
          { name: 'MySQL', purpose: 'قاعدة بيانات علائقية' },
          { name: 'Redis', purpose: 'كاش والجلسات' },
          { name: 'Eloquent ORM', purpose: 'نمذجة البيانات' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps والخدمات',
        technologies: [
          { name: 'Git', purpose: 'التحكم بإصدارات الكود' },
          { name: 'Composer/NPM', purpose: 'إدارة التبعيات' },
          { name: 'WhatsApp Business API', purpose: 'الإشعارات' },
          { name: 'نسخ احتياطي تلقائي', purpose: 'نسخ احتياطي يومي' }
        ]
      },
      
      // API Section
      apiTitle: 'API والتكاملات',
      apiSubtitle: 'نظام تكامل متقدم',
      
      apiStrategy: 'بنية API',
      strategyPoints: [
        {
          title: 'API REST',
          items: [
            'نقاط نهاية لإدارة الطلبات',
            'Webhooks للإشعارات',
            'توثيق Swagger كامل',
            'مصادقة بالتوكن',
            'حد معدل ذكي'
          ]
        },
        {
          title: 'التكاملات',
          items: [
            'WhatsApp Business API',
            'بوابة SMS للنسخ الاحتياطي',
            'إشعارات البريد الإلكتروني',
            'تصدير إلى Excel/PDF',
            'Webhooks خارجية'
          ]
        },
        {
          title: 'الأداء',
          items: [
            'وقت الاستجابة < 500ملث',
            'دعم WebSocket للوقت الفعلي',
            'كاش Redis للأداء',
            'ضغط GZIP',
            'موازنة الحمل'
          ]
        }
      ],
      
      performanceMetrics: 'المؤشرات الرئيسية API',
      metrics: [
        { metric: 'وقت استجابة API', value: '120ملث', target: '<500ملث', status: 'ممتاز' },
        { metric: 'التوفر', value: '99.9%', target: '>99.5%', status: 'ممتاز' },
        { metric: 'إشعارات الواتساب', value: '2ث', target: '<5ث', status: 'ممتاز' },
        { metric: 'المستخدمين المتزامنين', value: '50+', target: '>20', status: 'ممتاز' }
      ],
      
      // Results
      resultsTitle: 'التأثير والنتائج',
      resultsSubtitle: 'أداء تجاري قابل للقياس',
      
      achievements: 'الإنجازات الرئيسية',
      achievementsList: [
        {
          metric: 'الطلبات المدارة',
          description: 'إجمالي عدد الطلبات المعالجة',
          value: '286+',
          icon: ShoppingCart
        },
        {
          metric: 'الإشعارات',
          description: 'رسائل واتساب مرسلة تلقائيًا',
          value: '100%',
          icon: Bell
        },
        {
          metric: 'الكفاءة',
          description: 'الطلبات الجاهزة المعالجة',
          value: '93%',
          icon: CheckCircle
        },
        {
          metric: 'الأداء',
          description: 'نتيجة رضا المستخدم',
          value: '95/100',
          icon: Zap
        }
      ],
      
      clientTestimonial: {
        quote: 'المنصة أحدثت ثورة في إدارة طلباتنا. خفضت الإشعارات التلقائية بالواتساب من مكالمات المتابعة بنسبة 80% والعملاء يقدرون تتبع طلباتهم في الوقت الفعلي.',
        author: 'المسؤول التجاري',
        position: 'FPM المغرب'
      },
      
      keySuccess: 'عوامل النجاح',
      successPoints: [
        'واجهة بديهية تتطلب تدريبًا قليلاً',
        'إشعارات في الوقت الفعلي تقلل دعم العملاء',
        'لوحة تحكم كاملة لاتخاذ القرار',
        'أمان معزز لبيانات العملاء',
        'دعم سريع وتحسينات مستمرة'
      ],
      
      projectInfo: {
        status: 'في الإنتاج',
        team: 'مطور منفرد',
        stack: 'Laravel, MySQL, WhatsApp API'
      },
      
      // Video section
      playVideo: 'تشغيل التجربة',
      pauseVideo: 'إيقاف مؤقت',
      videoPlaying: 'جاري التشغيل...'
    }
  };

  const t = content[language];

  const sections = [
    { id: 'overview', label: t.navOverview, icon: Eye },
    { id: 'demo', label: t.navDemo, icon: Video },
    { id: 'features', label: t.navFeatures, icon: Package },
    { id: 'tech', label: t.navTech, icon: Code },
    { id: 'seo', label: t.navSeo, icon: Search },
    { id: 'results', label: t.navResults, icon: TrendingUp }
  ];

  const renderSectionContent = () => {
    switch(activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.overviewTitle}</h2>
              <p className={`text-base md:text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.overviewIntro}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.projectGoals}</h3>
              <div className="space-y-3">
                {t.goalsList.map((goal, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Target size={20} className="text-blue-500 flex-shrink-0 mt-1" />
                    <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{goal}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.myRole}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {t.roleDetails.map((detail, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'demo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.demoTitle}</h2>
              <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.demoSubtitle}
              </p>
            </div>

            {/* Video Demo */}
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold mb-2 text-blue-500">{t.videoTitle}</h3>
                <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.videoDescription}
                </p>
              </div>
              
              {/* Video Player */}
              <div className={`aspect-video rounded-xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-700' : 'border-gray-300'} mb-4`}>
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  controls
                  onPlay={() => setPlayingVideo(true)}
                  onPause={() => setPlayingVideo(false)}
                  poster="/thumbnails/demo-fpm.jpg"
                >
                  <source src="/video/demo-fpm.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleVideoPlay}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    playingVideo
                      ? 'bg-blue-600 text-white'
                      : darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {playingVideo ? (
                    <>
                      <Pause size={20} />
                      <span className="hidden sm:inline">{t.pauseVideo}</span>
                      <span className="sm:hidden">Pause</span>
                    </>
                  ) : (
                    <>
                      <Play size={20} />
                      <span>{t.playVideo}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleVideoPause}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  <Pause size={20} />
                  <span>{t.pauseVideo}</span>
                </button>
              </div>
              
              <div className="mt-6">
                <h4 className="text-lg md:text-xl font-bold mb-4 text-blue-500">{t.keyFeatures}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {t.keyFeaturesList.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.featuresTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.featuresSubtitle}
              </p>
            </div>

            <div className="space-y-6">
              {t.featuresList.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                    <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                      <div className="p-3 md:p-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 flex-shrink-0">
                        <Icon size={28} className="md:w-8 md:h-8" color="white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold mb-3 text-blue-500">{feature.title}</h3>
                        <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature.description}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {feature.details.map((detail, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'tech':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.techTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.techSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.frontendTech.title}</h3>
                <div className="space-y-4">
                  {t.frontendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.backendTech.title}</h3>
                <div className="space-y-4">
                  {t.backendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.devOpsTech.title}</h3>
                <div className="space-y-4">
                  {t.devOpsTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Architecture API WhatsApp</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Bell size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Gestion des Messages</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Templates et historique</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Server size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">API Backend</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Intégration WhatsApp Business</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <ShieldCheck size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Sécurité</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tokens et authentification</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'seo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.apiTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.apiSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.apiStrategy}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {t.strategyPoints.map((strategy, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-bold text-lg mb-4 text-blue-500">{strategy.title}</h4>
                    <div className="space-y-2">
                      {strategy.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.performanceMetrics}</h3>
              <div className="space-y-4">
                {t.metrics.map((metric, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {metric.metric}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        metric.status === 'excellent' 
                          ? 'bg-green-500/20 text-green-600'
                          : 'bg-yellow-500/20 text-yellow-600'
                      }`}>
                        {metric.value}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm gap-1">
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-600'}>
                        Cible: {metric.target}
                      </span>
                      <span className="text-blue-500 font-bold">✓ Atteint</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.resultsTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.resultsSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.achievements}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {t.achievementsList.map((achievement, index) => {
                  const Icon = achievement.icon;
                  return (
                    <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                      <Icon size={32} className="mx-auto mb-3 text-blue-500" />
                      <div className="text-lg md:text-xl font-bold mb-2 text-blue-500">{achievement.metric}</div>
                      <p className={`text-xs md:text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {achievement.description}
                      </p>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-600`}>
                        {achievement.value}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Témoignage Client</h3>
              <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                <MessageCircle size={32} className="text-blue-500 mb-4" />
                <blockquote className={`text-lg md:text-xl italic leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{t.clientTestimonial.quote}"
                </blockquote>
                <p className="font-bold text-lg">{t.clientTestimonial.author}</p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                  {t.clientTestimonial.position}
                </p>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.keySuccess}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.successPoints.map((point, index) => (
                  <div key={index} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex items-start gap-3`}>
                    <CheckCircle size={24} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-4 md:py-8`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-6 px-3 py-2 md:px-4 md:py-2 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } transition-colors shadow-lg text-sm md:text-base`}
        >
          <ArrowLeft size={18} className="md:w-5 md:h-5" />
          <span>{t.backButton}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className={`text-base sm:text-lg md:text-xl ${darkMode ? 'text-gray-400' : 'text-gray-600'} px-2`}>
            {t.subtitle}
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
          {t.stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`p-4 md:p-6 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <Icon size={20} className="mx-auto mb-2 text-blue-500 md:w-6 md:h-6" />
                <div className="text-lg md:text-2xl font-bold text-blue-500">{stat.value}</div>
                <div className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="mb-6 md:mb-8 overflow-x-auto">
          <div className="flex gap-1 md:gap-2 pb-2 min-w-max">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-lg font-semibold transition-all whitespace-nowrap text-sm md:text-base ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : darkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
                  }`}
                >
                  <Icon size={16} className="md:w-5 md:h-5" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="mb-8 md:mb-12">
          {renderSectionContent()}
        </div>

        {/* Project Info */}
        <div className={`mt-6 md:mt-8 p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-center">
            <div>
              <CheckCircle size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.status}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Statut</div>
            </div>
            <div>
              <UsersIcon size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.team}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Équipe</div>
            </div>
            <div>
              <Code size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.stack}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stack</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}